import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, Package, Phone, Home, Copy, Check } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';

export function OrderConfirmationPage() {
    const { orderNumber } = useParams<{ orderNumber: string }>();
    const [copied, setCopied] = useState(false);

    const handleCopyOrderNumber = async () => {
        if (orderNumber) {
            await navigator.clipboard.writeText(orderNumber);
            setCopied(true);
            toast.success('Numéro de commande copié!');
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-12 md:py-20">
            <div className="container-custom">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-lg mx-auto text-center"
                >
                    {/* Success Animation */}
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                        className="w-24 h-24 bg-success-50 rounded-full flex items-center justify-center mx-auto mb-8"
                    >
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.4, type: 'spring', stiffness: 200 }}
                        >
                            <CheckCircle2 className="w-12 h-12 text-success-500" />
                        </motion.div>
                    </motion.div>

                    {/* Heading */}
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="font-display font-bold text-2xl md:text-3xl text-secondary-900 mb-4"
                    >
                        Commande Confirmée!
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-gray-600 mb-8"
                    >
                        Merci pour votre commande. Vous recevrez un appel de confirmation sous peu.
                    </motion.p>

                    {/* Order Number */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                        className="bg-white rounded-2xl p-6 shadow-soft mb-8"
                    >
                        <p className="text-sm text-gray-500 mb-2">Numéro de commande</p>
                        <div className="flex items-center justify-center gap-3">
                            <p className="font-display font-bold text-xl text-primary-500">
                                {orderNumber}
                            </p>
                            <button
                                onClick={handleCopyOrderNumber}
                                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                                aria-label="Copier le numéro"
                            >
                                {copied ? (
                                    <Check className="w-4 h-4 text-success-500" />
                                ) : (
                                    <Copy className="w-4 h-4 text-gray-500" />
                                )}
                            </button>
                        </div>
                    </motion.div>

                    {/* What's Next */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6 }}
                        className="bg-white rounded-2xl p-6 shadow-soft mb-8"
                    >
                        <h2 className="font-display font-semibold text-lg text-secondary-900 mb-4">
                            Prochaines Étapes
                        </h2>

                        <div className="space-y-4 text-left">
                            <Step
                                icon={<Phone className="w-5 h-5" />}
                                title="Confirmation par téléphone"
                                description="Notre équipe vous contactera pour confirmer votre commande"
                            />
                            <Step
                                icon={<Package className="w-5 h-5" />}
                                title="Préparation"
                                description="Votre commande sera préparée et expédiée"
                            />
                            <Step
                                icon="🚚"
                                title="Livraison"
                                description="Vous recevrez votre colis et payerez à la livraison"
                            />
                        </div>
                    </motion.div>

                    {/* Contact Info */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                        className="bg-primary-50 rounded-2xl p-6 mb-8"
                    >
                        <p className="text-primary-700 text-sm">
                            <strong>Besoin d'aide?</strong>
                            <br />
                            Contactez-nous au{' '}
                            <a href="tel:+213555123456" className="font-semibold underline">
                                +213 555 123 456
                            </a>
                        </p>
                    </motion.div>

                    {/* Actions */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 }}
                        className="flex flex-col sm:flex-row gap-3 justify-center"
                    >
                        <Link to="/" className="btn-primary">
                            <Home className="w-5 h-5" />
                            Retour à l'Accueil
                        </Link>
                    </motion.div>
                </motion.div>
            </div>
        </div>
    );
}

// Step component
function Step({
    icon,
    title,
    description,
}: {
    icon: React.ReactNode | string;
    title: string;
    description: string;
}) {
    return (
        <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
                {typeof icon === 'string' ? (
                    <span className="text-lg">{icon}</span>
                ) : (
                    <span className="text-primary-500">{icon}</span>
                )}
            </div>
            <div>
                <h3 className="font-medium text-secondary-900">{title}</h3>
                <p className="text-sm text-gray-500">{description}</p>
            </div>
        </div>
    );
}
